import { Button, Grid, TextField, useMediaQ } from '@csp/csp-web-ui';
import { FC, useRef } from 'react';
import { useEffectOnce } from 'react-use';
import { useBarcodeInput } from '../../hooks/useBarcodeInput';
import { BarcodeInputContainerTexts } from '../../model/BarcodeInputContainerTexts';
import { BarcodeType } from '../../model/BarcodeType';
import { BarcodeScanningAvailability } from '../BarcodeScanningAvailability';

type In = {
  barcodeTypes?: BarcodeType[];
  onSubmit(sampleCode: string, type: BarcodeType): void;
  texts: BarcodeInputContainerTexts;
  isDisabled?: boolean;
  floatingHelperText?: boolean;
  ignoreBlocking?: boolean;
};

export const BarcodeInputContainer: FC<In> = ({
  onSubmit,
  texts,
  isDisabled = false,
  barcodeTypes,
  floatingHelperText = false,
  ignoreBlocking = false,
}) => {
  const shouldShowScanningContent = useMediaQ('sm', 'up');

  const {
    handleOnChange,
    handleOnTextFieldKeydown,
    submitTextField,
    textFieldValue,
    error,
    isScanningAvailable,
    isValid,
  } = useBarcodeInput({ onDetected: onSubmit, barcodeTypes, ignoreBlocking, isDisabled });

  useEffectOnce(() => {
    setTimeout(() => {
      ref.current?.focus();
    }, 100);
  });

  const ref = useRef<HTMLInputElement>();

  return (
    <Grid container alignItems={'flex-start'} columnGap={1}>
      {shouldShowScanningContent && (
        <Grid item xs="auto">
          <BarcodeScanningAvailability isScanningAvailable={isScanningAvailable} />
        </Grid>
      )}
      <Grid item xs>
        <TextField
          inputRef={ref}
          value={textFieldValue}
          placeholder={
            isScanningAvailable && shouldShowScanningContent
              ? texts.textFieldPlaceholder.withScan
              : texts.textFieldPlaceholder.withoutScan
          }
          onChange={handleOnChange}
          onKeyDown={handleOnTextFieldKeydown}
          {...(floatingHelperText && {
            FormHelperTextProps: {
              sx: {
                position: 'absolute',
                top: '100%',
              },
            },
          })}
          InputProps={{
            endAdornment: (
              <Button
                sx={{ borderRadius: '2px', height: '40px' }}
                onClick={submitTextField}
                disabled={!isValid}
                variant="contained"
                data-testid="add-sample-button"
              >
                {texts.addButtonLabel}
              </Button>
            ),
            sx: { paddingRight: 0 },
          }}
          helperText={error ? error : ' '}
          error={!!error}
          fullWidth
          size="small"
          disabled={isDisabled}
        />
      </Grid>
    </Grid>
  );
};
